// src/SchoolLogin.js
import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  GraduationCap,
  Users,
  BookOpen,
  ShieldCheck,
  AlertCircle,
  Sun,
  Moon,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * 🔧 ENV CONFIG (CRA-friendly)
 * Set these in your .env file at the project root:
 *
 *   REACT_APP_AUTH_API_BASE=https://app.schoolmasterhub.net
 *   REACT_APP_PUBLIC_EMAIL_API_TOKEN=your-public-token-here
 *
 * Then restart `npm start`.
 */
const AUTH_API_BASE =
  process.env.REACT_APP_AUTH_API_BASE || "https://app.schoolmasterhub.net";

const PUBLIC_EMAIL_TOKEN =
  process.env.REACT_APP_PUBLIC_EMAIL_API_TOKEN || "";

// ---------- Forgot Password Modal ----------
function ForgotPasswordModal({ open, onClose }) {
  const [step, setStep] = useState("email"); // "email" | "verify" | "done"

  const [email, setEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [code, setCode] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const codeOk = code.trim().length >= 4;
  const passwordOk =
    newPassword.trim().length >= 8 && newPassword === confirmPassword;

  const handleClose = () => {
    // Reset everything when closing
    setStep("email");
    setEmail("");
    setOtpToken("");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setSubmitting(false);
    setError("");
    onClose?.();
  };

  // 1️⃣ Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!emailOk) return;

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(`${AUTH_API_BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(PUBLIC_EMAIL_TOKEN
            ? { "x-smh-token": PUBLIC_EMAIL_TOKEN }
            : {}),
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(
          data.error || "Failed to send verification code. Please try again."
        );
      }

      setOtpToken(data.token || "");
      setStep("verify");
    } catch (err) {
      console.error("[ForgotPassword] send-otp error:", err);
      setError(
        err?.message ||
          "Could not send the verification code. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // 2️⃣ Verify OTP + reset password (calls your ORDS forgot_password API via Next.js)
  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    if (!emailOk || !codeOk || !passwordOk || !otpToken) return;

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(
        `${AUTH_API_BASE}/api/auth/reset-password-with-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(PUBLIC_EMAIL_TOKEN
              ? { "x-smh-token": PUBLIC_EMAIL_TOKEN }
              : {}),
          },
          body: JSON.stringify({
            email: email.trim(),
            code: code.trim(),
            token: otpToken,
            newPassword: newPassword.trim(),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(
          data.error || "Failed to reset password. Please try again."
        );
      }

      setStep("done");
    } catch (err) {
      console.error("[ForgotPassword] reset-password-with-otp error:", err);
      setError(
        err?.message ||
          "Could not reset the password at this time. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/90 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl p-6 sm:p-7 relative">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-100"
        >
          ✕
        </button>

        {/* STEP 1: ENTER EMAIL */}
        {step === "email" && (
          <>
            <h2 className="text-lg sm:text-xl font-semibold">
              Reset password
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 mt-1">
              Enter the email linked to your School Master Hub account. We will
              send a 6-digit verification code.
            </p>

            <form onSubmit={handleSendOtp} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs sm:text-sm text-slate-200 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError("");
                    }}
                    className="pl-9 pr-3 py-2.5 w-full border rounded-lg bg-slate-900 text-xs sm:text-sm text-slate-50 border-slate-700 focus:ring-2 focus:ring-indigo-500"
                    placeholder="example@school.edu"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="text-xs sm:text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded px-3 py-2 flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-xs sm:text-sm text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!emailOk || submitting}
                  className="text-xs sm:text-sm bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send code"}
                </button>
              </div>
            </form>
          </>
        )}

        {/* STEP 2: ENTER CODE + NEW PASSWORD */}
        {step === "verify" && (
          <>
            <h2 className="text-lg sm:text-xl font-semibold">
              Verify code & set new password
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 mt-1">
              A 6-digit verification code has been sent to{" "}
              <span className="font-medium">{email}</span>. Enter the code and
              choose a new password.
            </p>

            <form onSubmit={handleVerifyAndReset} className="mt-4 space-y-4">
              {/* CODE */}
              <div>
                <label className="block text-xs sm:text-sm text-slate-200 mb-1">
                  Verification code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (error) setError("");
                  }}
                  className="px-3 py-2.5 w-full border rounded-lg bg-slate-900 text-xs sm:text-sm text-slate-50 border-slate-700 focus:ring-2 focus:ring-indigo-500 tracking-[0.3em]"
                  placeholder="123456"
                  maxLength={6}
                  required
                />
              </div>

              {/* NEW PASSWORD */}
              <div>
                <label className="block text-xs sm:text-sm text-slate-200 mb-1">
                  New password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError("");
                  }}
                  className="px-3 py-2.5 w-full border rounded-lg bg-slate-900 text-xs sm:text-sm text-slate-50 border-slate-700 focus:ring-2 focus:ring-indigo-500"
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              {/* CONFIRM PASSWORD */}
              <div>
                <label className="block text-xs sm:text-sm text-slate-200 mb-1">
                  Confirm new password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError("");
                  }}
                  className="px-3 py-2.5 w-full border rounded-lg bg-slate-900 text-xs sm:text-sm text-slate-50 border-slate-700 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Re-enter new password"
                  required
                />
                {newPassword &&
                  confirmPassword &&
                  newPassword !== confirmPassword && (
                    <p className="mt-1 text-[11px] text-rose-300">
                      Passwords do not match.
                    </p>
                  )}
              </div>

              {error && (
                <div className="text-xs sm:text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded px-3 py-2 flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setOtpToken("");
                    setCode("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setError("");
                  }}
                  className="text-xs sm:text-sm text-slate-300 hover:text-white"
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={!emailOk || !codeOk || !passwordOk || submitting}
                  className="text-xs sm:text-sm bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Updating..." : "Update password"}
                </button>
              </div>
            </form>
          </>
        )}

        {/* STEP 3: DONE */}
        {step === "done" && (
          <div className="space-y-3 pt-2 pb-1">
            <h2 className="text-lg sm:text-xl font-semibold">
              Password updated
            </h2>
            <p className="text-xs sm:text-sm text-slate-300">
              Your password has been reset successfully. You can now sign in
              with your new password.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm px-4 py-2 rounded-lg text-white mt-1"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const DEMO_SCHOOL_ID = 101;

// ---------- Main Login Page ----------
export default function SchoolLogin() {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailOk && password.trim().length > 0 && !isLoading;

  // THEME-DEPENDENT STYLING
  const cardBase =
    "rounded-2xl shadow-xl p-6 sm:p-7 border transition-colors duration-200";
  const cardTheme = darkMode
    ? "bg-slate-900/95 border-slate-800 text-slate-100"
    : "bg-white border-slate-200 text-slate-900";

  const subtleText = darkMode ? "text-slate-400" : "text-slate-500";
  const labelText = darkMode ? "text-slate-200" : "text-slate-700";

  const inputBase =
    "pl-9 pr-3 py-2.5 w-full border rounded-lg focus:ring-2 focus:ring-indigo-500 text-xs sm:text-sm";
  const inputTheme = darkMode
    ? "bg-slate-950 text-slate-50 border-slate-700"
    : "bg-slate-50 text-slate-900 border-slate-300";

  const rightPanelBg = darkMode ? "bg-slate-950/30" : "bg-slate-50";

  const loginButtonEnabled = "bg-indigo-600 hover:bg-indigo-700";
  const loginButtonDisabled = "bg-indigo-500/60 cursor-not-allowed";

  const toggleIconWrapper = darkMode
    ? "bg-slate-800 text-slate-100 border-slate-700"
    : "bg-slate-100 text-slate-800 border-slate-300";

  const toggleLabel = darkMode ? "Dark" : "Light";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password.trim()) {
      setError("Please enter your password.");
      return;
    }

    const result = await login(email, password, null, false, DEMO_SCHOOL_ID);

    if (!result.success) {
      setError(result.error || "Invalid email or password.");
      return;
    }

    // ✅ Force navigation to dashboard on success
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-slate-100">
      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 to-sky-700 p-10 xl:p-12 flex-col justify-between text-white">
        <div>
          <div className="flex items-center space-x-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15">
              <GraduationCap className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">School Master Hub</h1>
              <p className="text-sm text-indigo-100">
                Empowering Education Through Technology
              </p>
            </div>
          </div>

          <h2 className="text-3xl xl:text-4xl font-bold mb-5 leading-tight">
            Streamline your school management in one intelligent hub.
          </h2>
          <p className="text-base xl:text-lg text-indigo-50/90 mb-10 max-w-xl">
            Student Management • Academic Planning • Secure &amp; Reliable
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 max-w-md">
          {[
            {
              Icon: Users,
              title: "Student Management",
              desc: "Comprehensive student profiles and tracking.",
            },
            {
              Icon: BookOpen,
              title: "Academic Planning",
              desc: "Curriculum management and lesson planning.",
            },
            {
              Icon: ShieldCheck,
              title: "Secure & Reliable",
              desc: "Enterprise-grade security for student data.",
            },
          ].map(({ Icon, title, desc }, idx) => (
            <div className="flex items-center space-x-4" key={idx}>
              <div className="flex-shrink-0 w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-sm xl:text-base">{title}</h3>
                <p className="text-indigo-50 text-xs xl:text-sm">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs xl:text-sm text-indigo-100/90 mt-6">
          © {new Date().getFullYear()} School Master Hub. All rights reserved.
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div
        className={`flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8 transition-colors duration-200 ${rightPanelBg}`}
      >
        <div className="w-full max-w-md">
          {/* MOBILE HEADER */}
          <div className="lg:hidden text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center space-x-2 text-indigo-300">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-indigo-500/10">
                <GraduationCap className="h-5 w-5" />
              </div>
              <span className="text-xl sm:text-2xl font-bold">
                School Master Hub
              </span>
            </div>
            <p className={`mt-1 text-[11px] sm:text-xs ${subtleText}`}>
              Empowering Education Through Technology
            </p>
          </div>

          {/* LOGIN CARD */}
          <div className={`${cardBase} ${cardTheme}`}>
            <div className="flex items-start justify-between mb-6">
              <div className="text-left">
                <h2 className="text-xl sm:text-2xl font-bold mb-1">
                  Welcome Back
                </h2>
                <p className={`text-xs sm:text-sm ${subtleText}`}>
                  Sign in to access your dashboard
                </p>
              </div>

              {/* THEME TOGGLE */}
              <button
                type="button"
                onClick={() => setDarkMode((v) => !v)}
                className={`ml-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] sm:text-xs font-medium ${toggleIconWrapper}`}
              >
                {darkMode ? (
                  <Moon className="h-3.5 w-3.5" />
                ) : (
                  <Sun className="h-3.5 w-3.5" />
                )}
                <span>{toggleLabel} mode</span>
              </button>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="mb-4">
                <label
                  className={`block text-xs sm:text-sm mb-1 ${labelText}`}
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError("");
                    }}
                    disabled={isLoading}
                    className={`${inputBase} ${inputTheme}`}
                    placeholder="example@school.edu"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-4">
                <label
                  className={`block text-xs sm:text-sm mb-1 ${labelText}`}
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError("");
                    }}
                    disabled={isLoading}
                    className={`${inputBase} ${inputTheme} pr-10`}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={isLoading}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-500"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px] sm:text-xs">
                <label
                  className={`inline-flex items-center gap-2 cursor-pointer ${subtleText}`}
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span>Remember this device</span>
                </label>

                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="hover:text-indigo-400 font-medium text-left sm:text-right"
                >
                  Forgot password?
                </button>
              </div>

              {/* LOGIN BUTTON */}
              <button
                type="submit"
                disabled={!canSubmit}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center transition ${
                  canSubmit ? loginButtonEnabled : loginButtonDisabled
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <>Sign In</>
                )}
              </button>
            </form>

            {/* ERROR */}
            {error && (
              <div className="mt-4 p-3 bg-rose-950/40 border border-rose-700 text-rose-200 text-xs sm:text-sm rounded flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* FREE TRIAL */}
            <div
              className={`mt-6 text-center text-xs sm:text-sm ${subtleText}`}
            >
              <p>Don&apos;t have an account?</p>
              <a
                href="https://www.schoolmasterhub.net/#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 font-semibold text-indigo-400 hover:underline inline-block"
              >
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
      />
    </div>
  );
}
